// Handmatige retour via de admin (blok D, opdracht 21-07). Volgt
// dezelfde weg als de publieke flow — zelfde lookup (zonder dagcap,
// dit zit achter de admin-login), zelfde keuzes, zelfde beslisboom en
// nazorg server-side — maar zonder de klantopmaak. Extra: een vrij
// opmerkingenveld dat in het retourrecord en het klantdossier landt.
//
// Sessie 171 (besluit JP 16-10, twee tikken, geen typen): het scherm
// opent ook al ingevuld vanuit de klantkaart (zie `retourLink.ts`), en
// zegt per keuze vooraf wat er gebeurt (uitkomst-vooraf). Het vinkje dat
// een uitsluiting opzij zet staat bij die uitkomst, niet in een los blok.

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, PackagePlus, Search, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  adminRetourenApi,
  vertaalApiFout,
  type AdminRetourAanmaakAntwoord,
  type AdminRetourAanmaakBody,
  type AdminRetourOrderzoekAntwoord,
  type AdminRetourVoorstel,
  type RetourArtikelKeuze,
  type RetourOplossingWaarde,
  type RetourRedenWaarde,
} from "@/lib/api";
import {
  ALLE_REDENEN,
  GEOPEND_GEVOELIGE_REDENEN,
  SCHADE_REDENEN,
  formatteerBedrag,
} from "@/pages/Retour/retourLogica";
import {
  kaart10Api,
  kaart10Sleutels,
} from "@/pages/WerkplekV6/Rechts/kaart10/api";
import { RETOUR_PARAMS, vindArtikel } from "./retourLink";

/** Melding die het dossier na het opslaan bovenaan laat zien. */
export interface RetourMeldingState {
  retourMelding?: { soort: "label" | "beoordeling"; tekst: string };
}

/** Na een korte pauze in het kiezen pas de nieuwe waarde (debounce). */
function useVertraagd<T>(waarde: T, ms: number): T {
  const [vertraagd, setVertraagd] = useState(waarde);
  useEffect(() => {
    const klok = setTimeout(() => setVertraagd(waarde), ms);
    return () => clearTimeout(klok);
  }, [waarde, ms]);
  return vertraagd;
}

interface RegelKeuze {
  geselecteerd: boolean;
  aantal: number;
  reden: RetourRedenWaarde | "";
  verpakkingGeopend: boolean | null;
  toelichting: string;
  toelichtingKeuze: string;
  omschrijvingDuidelijk: boolean | null;
}

const LEGE_REGEL: RegelKeuze = {
  geselecteerd: false,
  aantal: 1,
  reden: "",
  verpakkingGeopend: null,
  toelichting: "",
  toelichtingKeuze: "",
  omschrijvingDuidelijk: null,
};

/** Genoeg gekozen om de uitkomst vooraf te vragen: reden en, waar het
 *  telt, geopend of niet. De toelichting hoort daar niet bij. */
function regelBeslisbaar(regel: RegelKeuze): boolean {
  if (!regel.reden) return false;
  return !(
    GEOPEND_GEVOELIGE_REDENEN.includes(regel.reden) &&
    regel.verpakkingGeopend === null
  );
}

/** De gekozen regels in de vorm van de API (index = plek in het orderzoek). */
function gekozenArtikelen(regels: RegelKeuze[]): RetourArtikelKeuze[] {
  return regels
    .map((regel, index) => ({ regel, index }))
    .filter(({ regel }) => regel.geselecteerd)
    .map(({ regel, index }) => ({
      artikel_index: index,
      aantal: regel.aantal,
      reden: regel.reden as RetourRedenWaarde,
      verpakking_geopend: regel.verpakkingGeopend,
      toelichting: regel.toelichting.trim() || null,
      toelichting_keuze: regel.toelichtingKeuze || null,
      omschrijving_duidelijk: regel.omschrijvingDuidelijk,
    }));
}

function regelCompleet(regel: RegelKeuze): boolean {
  if (!regel.reden) return false;
  if (
    GEOPEND_GEVOELIGE_REDENEN.includes(regel.reden) &&
    regel.verpakkingGeopend === null
  ) {
    return false;
  }
  if (
    (SCHADE_REDENEN.includes(regel.reden) || regel.reden === "andere_reden") &&
    regel.toelichting.trim() === ""
  ) {
    return false;
  }
  if (regel.reden === "voldoet_niet_aan_verwachtingen") {
    if (!regel.toelichtingKeuze) return false;
    if (regel.omschrijvingDuidelijk === null) return false;
  }
  return true;
}

/** Admin-pagina "Retour aanmaken" (blok D). */
export function RetourAanmakenPagina() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [zoekParams] = useSearchParams();
  // Sessie 171: voorinvullen uit de klantkaart (zie retourLink.ts).
  const paramOrder = zoekParams.get(RETOUR_PARAMS.ordernummer) ?? "";
  const paramMail = zoekParams.get(RETOUR_PARAMS.mailadres) ?? "";
  const paramArtikel = zoekParams.get(RETOUR_PARAMS.artikel);
  const paramKlant = zoekParams.get(RETOUR_PARAMS.klant);
  const [ordernummer, setOrdernummer] = useState(paramOrder);
  const [email, setEmail] = useState(paramMail);
  /** Het artikel uit de url wordt één keer aangevinkt, bij de eerste zoek. */
  const artikelVoorkeur = useRef<string | null>(paramArtikel);
  const [order, setOrder] = useState<AdminRetourOrderzoekAntwoord | null>(
    null,
  );
  const [regels, setRegels] = useState<RegelKeuze[]>([]);
  const [oplossing, setOplossing] =
    useState<RetourOplossingWaarde>("terugbetaling");
  const [opmerking, setOpmerking] = useState("");

  // Besluit JP 14-09. Komt dit scherm uit een lopend mailgesprek
  // (?conversatie=<id>), dan staat "Klant niet mailen" standaard aan:
  // JP stuurt het label dan zelf mee in zijn eigen antwoord. Zonder dat
  // gesprek staat het uit en gaan de gewone retourmails gewoon.
  const conversatieId = zoekParams.get("conversatie");
  // Voorstel-modus (besluit JP 15-09): geopend vanuit "Aanpassen" boven
  // een mailconcept. Dan maakt dit scherm niets aan — het slaat de
  // voorbereide actie op en JP maakt het label echt door dat antwoord
  // te versturen. Zonder ?email= is dit het gewone aanmaakscherm.
  const voorstelEmailId = zoekParams.get("email");
  const voorstelModus = voorstelEmailId !== null;
  const [termijnOverschrijven, setTermijnOverschrijven] = useState(false);
  // Sessie 169 (C2): alleen de uitsluiting "verpakking geopend" opzij.
  const [geopendOverschrijven, setGeopendOverschrijven] = useState(false);
  // Sessie 169 (C1): de uitkomst blijft in het scherm staan als er iets
  // niet doorging (niet aangenomen, label mislukt). Geen vluchtige toast.
  const [resultaat, setResultaat] =
    useState<AdminRetourAanmaakAntwoord | null>(null);
  const [klantNietMailen, setKlantNietMailen] = useState(
    conversatieId !== null || voorstelEmailId !== null,
  );
  const [ookGeenStatusmails, setOokGeenStatusmails] = useState(false);

  const zoek = useMutation({
    mutationFn: (invoer: { ordernummer: string; email: string }) =>
      adminRetourenApi.orderzoek(invoer.ordernummer.trim(), invoer.email.trim()),
    onSuccess: (antwoord) => {
      setOrder(antwoord);
      setResultaat(null);
      setGeopendOverschrijven(false);
      setTermijnOverschrijven(false);
      const voorkeur = artikelVoorkeur.current;
      artikelVoorkeur.current = null;
      const aangevinkt =
        voorkeur !== null
          ? vindArtikel(
              antwoord.artikelen.map((a) => a.naam),
              voorkeur,
            )
          : -1;
      setRegels(
        antwoord.artikelen.map((artikel, index) =>
          index === aangevinkt
            ? {
                ...LEGE_REGEL,
                geselecteerd: true,
                aantal: Math.max(1, artikel.aantal),
              }
            : { ...LEGE_REGEL },
        ),
      );
    },
    onError: (fout) => toast.error(vertaalApiFout(fout)),
  });
  const zoekOrder = zoek.mutate;

  // Ordernummer en mailadres in de url: meteen opzoeken, één keer.
  const automatischGezocht = useRef(false);
  useEffect(() => {
    if (automatischGezocht.current) return;
    if (paramOrder.trim().length < 3 || paramMail.trim().length < 3) return;
    automatischGezocht.current = true;
    zoekOrder({ ordernummer: paramOrder, email: paramMail });
  }, [paramOrder, paramMail, zoekOrder]);

  // Alleen klant en mailadres (knop "Retour" op de klantkaart): de
  // laatste bestellingen om uit te kiezen; bij één meteen die.
  const klantBestellingen = useQuery({
    queryKey: kaart10Sleutels.bestellingen(paramKlant ?? ""),
    queryFn: () => kaart10Api.bestellingen(paramKlant as string),
    enabled: Boolean(paramKlant) && !paramOrder,
    staleTime: 60_000,
  });
  const keuzeOrders = (klantBestellingen.data?.orders ?? []).slice(0, 5);
  function kiesBestelling(nummer: string) {
    setOrdernummer(nummer);
    zoekOrder({ ordernummer: nummer, email });
  }
  const eenBestellingGekozen = useRef(false);
  useEffect(() => {
    if (eenBestellingGekozen.current || paramOrder) return;
    const orders = klantBestellingen.data?.orders;
    if (!orders || orders.length !== 1 || paramMail.trim().length < 3) return;
    eenBestellingGekozen.current = true;
    setOrdernummer(orders[0].ordernummer);
    zoekOrder({ ordernummer: orders[0].ordernummer, email: paramMail });
  }, [klantBestellingen.data, paramOrder, paramMail, zoekOrder]);

  function naarDossier(
    retourId: string,
    melding?: RetourMeldingState["retourMelding"],
  ) {
    const state: RetourMeldingState | undefined = melding
      ? { retourMelding: melding }
      : undefined;
    navigate(`/retouren?retour=${encodeURIComponent(retourId)}`, { state });
  }

  const aanmaken = useMutation<
    AdminRetourAanmaakAntwoord | AdminRetourVoorstel,
    unknown,
    void
  >({
    mutationFn: async () => {
      if (!order) throw new Error("geen order");
      const artikelen = gekozenArtikelen(regels);
      const gedeeld = {
        order_id: order.order_id,
        artikelen,
        oplossing,
        opmerking: opmerking.trim() || null,
        termijn_overschrijven: termijnOverschrijven,
        geopend_overschrijven: geopendOverschrijven,
        klant_niet_mailen: klantNietMailen,
        ook_geen_statusmails: ookGeenStatusmails,
      };
      if (voorstelEmailId) {
        return adminRetourenApi.voorstelOpslaan({
          ...gedeeld,
          email_id: voorstelEmailId,
        });
      }
      return adminRetourenApi.aanmaken({
        ...gedeeld,
        conversatie_id: conversatieId,
      });
    },
    onSuccess: (uitkomst) => {
      if (voorstelEmailId) {
        toast.success(t("admin.retouren.voorstel_opgeslagen"));
        navigate(`/?mail=${voorstelEmailId}`);
        return;
      }
      const retour = uitkomst as AdminRetourAanmaakAntwoord;
      // Sessie 171: de melding staat daarna bovenaan het dossier, vast
      // in beeld; de toast blijft erbij als extra.
      if (retour.uitkomst === "label_gemaakt") {
        setResultaat(null);
        const tekst = t("admin.retouren.melding_label_gemaakt", {
          nummer: retour.retournummer,
        });
        toast.success(tekst);
        naarDossier(retour.id, { soort: "label", tekst });
        return;
      }
      if (retour.uitkomst === "beoordeling") {
        setResultaat(null);
        const tekst = t("admin.retouren.melding_beoordeling", {
          nummer: retour.retournummer,
          reden: retour.reden ?? t("admin.retouren.uitkomst_reden_onbekend"),
        });
        toast.message(tekst, { duration: 10_000 });
        naarDossier(retour.id, { soort: "beoordeling", tekst });
        return;
      }
      // niet_aangenomen en label_mislukt: blijven staan in het scherm.
      setResultaat(retour);
    },
    // Voorstel-modus: een afwijzing ("Niet aangenomen ...") moet lang
    // genoeg blijven staan om te lezen.
    onError: (fout) =>
      toast.error(vertaalApiFout(fout), voorstelEmailId ? { duration: 15_000 } : undefined),
  });

  // Sessie 171: de melding na opslaan staat boven het formulier en
  // schuift in beeld, nooit onderin achter de tabbalk.
  const meldingPlek = useRef<HTMLDivElement | null>(null);
  const foutNaOpslaan = aanmaken.isError;
  useEffect(() => {
    if (!resultaat && !foutNaOpslaan) return;
    meldingPlek.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  }, [resultaat, foutNaOpslaan]);

  // Sessie 171: de uitkomst vooraf, zodra elke gekozen regel een reden
  // (en waar nodig geopend of niet) heeft. Licht vertraagd, schrijft niets.
  const voorafSleutel = useMemo(() => {
    if (!order) return null;
    const gekozenRegels = regels.filter((r) => r.geselecteerd);
    if (gekozenRegels.length === 0) return null;
    if (!gekozenRegels.every(regelBeslisbaar)) return null;
    const body: AdminRetourAanmaakBody = {
      order_id: order.order_id,
      artikelen: gekozenArtikelen(regels),
      oplossing,
      termijn_overschrijven: termijnOverschrijven,
      geopend_overschrijven: geopendOverschrijven,
    };
    return JSON.stringify(body);
  }, [order, regels, oplossing, termijnOverschrijven, geopendOverschrijven]);
  const vertraagdeSleutel = useVertraagd(voorafSleutel, 300);
  const voorafActueel =
    voorafSleutel !== null && vertraagdeSleutel === voorafSleutel;
  const vooraf = useQuery({
    queryKey: ["admin", "retour-uitkomst-vooraf", vertraagdeSleutel],
    queryFn: () =>
      adminRetourenApi.uitkomstVooraf(
        JSON.parse(vertraagdeSleutel as string) as AdminRetourAanmaakBody,
      ),
    enabled: voorafActueel,
    staleTime: 30_000,
    retry: false,
  });
  const voorafData = voorafActueel ? vooraf.data : undefined;
  const toonGeopendVinkje =
    geopendOverschrijven ||
    (voorafData?.uitsluiting === "geopend" &&
      voorafData.geopend_overschrijfbaar);
  const toonTermijnVinkje =
    termijnOverschrijven || voorafData?.termijn_verstreken === true;

  // Sessie 169 (C4): nieuwe labelpoging na label_mislukt.
  const opnieuw = useMutation({
    mutationFn: (retourId: string) => adminRetourenApi.labelOpnieuw(retourId),
    onSuccess: (retour) => {
      toast.success(
        t("admin.retouren.uitkomst_label_gemaakt", {
          nummer: retour.retournummer,
        }),
      );
      naarDossier(retour.id);
    },
  });

  const geopendHint =
    resultaat?.uitkomst === "niet_aangenomen" &&
    ((resultaat.reden ?? "").toLowerCase().includes("geopend") ||
      regels.some((r) => r.geselecteerd && r.verpakkingGeopend === true));

  const gekozen = regels.filter((regel) => regel.geselecteerd);
  const compleet =
    gekozen.length > 0 && gekozen.every((regel) => regelCompleet(regel));
  // Regelnummers (1-gebaseerd) die nog iets missen — voor de uitleg
  // onder de knop (hotfix 14-09).
  const onvolledigeRegels = regels
    .map((regel, index) => ({ regel, nummer: index + 1 }))
    .filter(({ regel }) => regel.geselecteerd && !regelCompleet(regel))
    .map(({ nummer }) => nummer);

  function voorafTekst(): string | null {
    if (!voorafData) return null;
    const reden =
      voorafData.reden ?? t("admin.retouren.uitkomst_reden_onbekend");
    if (voorafData.uitsluiting === "geopend")
      return t("admin.retouren.vooraf_geopend");
    if (voorafData.uitsluiting === "termijn")
      return t("admin.retouren.vooraf_termijn");
    if (voorafData.uitkomst === "niet_aangenomen")
      return t("admin.retouren.vooraf_niet_aangenomen", { reden });
    if (voorafData.uitkomst === "beoordeling")
      return t("admin.retouren.vooraf_beoordeling", { reden });
    return t("admin.retouren.vooraf_label");
  }

  function wijzigRegel(index: number, wijziging: Partial<RegelKeuze>) {
    setRegels((huidige) =>
      huidige.map((regel, i) =>
        i === index ? { ...regel, ...wijziging } : regel,
      ),
    );
  }

  return (
    <div className="min-w-0 max-w-full space-y-5">
      <header className="flex items-center gap-3">
        <Button
          asChild
          variant="ghost"
          size="icon"
          aria-label={t("algemeen.terug")}
        >
          <Link to="/retouren">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <PackagePlus className="h-5 w-5 text-brand-cyan-dark" aria-hidden />
        <div>
          <h1 className="text-[20px] font-bold">
            {voorstelModus
              ? t("admin.retouren.voorstel_titel")
              : t("admin.retouren.aanmaken_titel")}
          </h1>
          {voorstelModus ? (
            <p className="text-[13px] text-muted-foreground">
              {t("admin.retouren.voorstel_uitleg")}
            </p>
          ) : null}
        </div>
      </header>

      {/* Sessie 171: de uitkomst na opslaan staat hier, boven het
          formulier, en schuift in beeld. Onderin een lange pagina viel
          hij op de telefoon achter de tabbalk. */}
      <div
        ref={meldingPlek}
        data-testid="melding-bovenaan"
        className="scroll-mt-4 space-y-2 empty:hidden"
      >
        {/* Een mislukte aanmaak bleef alleen een vluchtige toast.
            Die mis je op een telefoon; deze melding blijft staan. */}
        {resultaat?.uitkomst === "niet_aangenomen" && (
          <div
            role="alert"
            data-testid="uitkomst-niet-aangenomen"
            className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[12.5px] text-red-900"
          >
            <div className="font-semibold mb-0.5">
              {t("admin.retouren.uitkomst_niet_aangenomen_titel")}
            </div>
            <p>
              {t("admin.retouren.uitkomst_niet_aangenomen", {
                reden:
                  resultaat.reden ??
                  t("admin.retouren.uitkomst_reden_onbekend"),
              })}
            </p>
            {geopendHint && (
              <p className="mt-1" data-testid="hint-geopend">
                {t("admin.retouren.uitkomst_niet_aangenomen_geopend_hint", {
                  nummer: resultaat.retournummer,
                })}
              </p>
            )}
          </div>
        )}
        {resultaat?.uitkomst === "label_mislukt" && (
          <div
            role="alert"
            data-testid="uitkomst-label-mislukt"
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900 space-y-2"
          >
            <div className="font-semibold">
              {t("admin.retouren.uitkomst_label_mislukt_titel")}
            </div>
            <p>
              {t("admin.retouren.uitkomst_label_mislukt", {
                fout:
                  resultaat.label_fout ??
                  t("admin.retouren.uitkomst_reden_onbekend"),
              })}
            </p>
            {klantNietMailen && (
              <p data-testid="label-mislukt-mail">
                {t("admin.retouren.uitkomst_label_mislukt_mail")}
              </p>
            )}
            {opnieuw.isError && (
              <p data-testid="opnieuw-fout" className="text-red-900">
                {t("admin.retouren.opnieuw_mislukt", {
                  fout: vertaalApiFout(opnieuw.error),
                })}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                onClick={() => opnieuw.mutate(resultaat.id)}
                disabled={opnieuw.isPending}
                data-testid="knop-opnieuw-proberen"
              >
                {opnieuw.isPending
                  ? t("admin.retouren.opnieuw_bezig")
                  : t("admin.retouren.opnieuw_proberen")}
              </Button>
              <Link
                to={`/retouren?retour=${encodeURIComponent(resultaat.id)}`}
                className="underline"
                data-testid="link-naar-dossier"
              >
                {t("admin.retouren.naar_dossier")}
              </Link>
            </div>
          </div>
        )}
        {aanmaken.isError && (
          <div
            role="alert"
            data-testid="aanmaak-fout"
            className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[12.5px] text-red-900"
          >
            <div className="font-semibold mb-0.5">
              {t("admin.retouren.aanmaak_mislukt_titel")}
            </div>
            <p>{vertaalApiFout(aanmaken.error)}</p>
          </div>
        )}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-[15px]">
            {t("admin.retouren.aanmaken_zoek_titel")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Sessie 171: knop "Retour" op de klantkaart. Eén tik op de
              bestelling en het formulier zoekt hem op. */}
          {paramKlant && !paramOrder && !order ? (
            <div className="mb-3 space-y-1.5" data-testid="kies-bestelling">
              <div className="text-[12px] font-medium">
                {t("admin.retouren.kies_bestelling_titel")}
              </div>
              {klantBestellingen.isLoading ? (
                <p className="text-[12.5px] text-muted-foreground">
                  {t("admin.retouren.kies_bestelling_laden")}
                </p>
              ) : klantBestellingen.isError ? (
                <p className="text-[12.5px] text-amber-800" role="alert">
                  {t("admin.retouren.kies_bestelling_fout")}
                </p>
              ) : keuzeOrders.length === 0 ? (
                <p className="text-[12.5px] text-muted-foreground">
                  {t("admin.retouren.kies_bestelling_leeg")}
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {keuzeOrders.map((o) => (
                    <li key={o.ordernummer}>
                      <button
                        type="button"
                        className="w-full min-w-0 rounded-md border px-3 py-2 text-left text-[13px] hover:bg-slate-50 disabled:opacity-60"
                        data-bestelling={o.ordernummer}
                        disabled={zoek.isPending || email.trim().length < 3}
                        onClick={() => kiesBestelling(o.ordernummer)}
                      >
                        <span className="font-medium">{o.ordernummer}</span>
                        {o.besteld_op
                          ? ` · ${new Date(o.besteld_op).toLocaleDateString("nl-NL")}`
                          : ""}
                        {` · ${formatteerBedrag(o.bedrag_cents)}`}
                        {o.producten.length > 0 ? (
                          <span className="block truncate text-[12px] text-muted-foreground">
                            {o.producten.join(", ")}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              zoek.mutate({ ordernummer, email });
            }}
          >
            <div className="w-full min-w-0 space-y-1 sm:w-auto">
              <Label htmlFor="aanmaak-ordernummer" className="text-[12px]">
                {t("retour.stap1.ordernummer")}
              </Label>
              <Input
                id="aanmaak-ordernummer"
                value={ordernummer}
                onChange={(e) => setOrdernummer(e.target.value)}
                placeholder="ORD26… of ORD26…-01"
                className="w-full max-w-full sm:w-56"
              />
            </div>
            <div className="w-full min-w-0 space-y-1 sm:w-auto">
              <Label htmlFor="aanmaak-email" className="text-[12px]">
                {t("retour.stap1.email")}
              </Label>
              <Input
                id="aanmaak-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full max-w-full sm:w-64"
              />
            </div>
            <Button
              type="submit"
              disabled={
                zoek.isPending ||
                ordernummer.trim().length < 3 ||
                email.trim().length < 3
              }
            >
              <Search className="mr-1.5 h-4 w-4" aria-hidden />
              {t("admin.retouren.aanmaken_zoek_knop")}
            </Button>
          </form>
          {order && order.bestaande_retournummers.length > 0 && (
            <p className="mt-2 text-[12.5px] text-amber-700">
              {t("admin.retouren.aanmaken_bestaande", {
                nummers: order.bestaande_retournummers.join(", "),
              })}
            </p>
          )}
          {order && !order.binnen_termijn && (
            <p className="mt-2 text-[12.5px] text-amber-700">
              {t("admin.retouren.aanmaken_buiten_termijn")}
            </p>
          )}
        </CardContent>
      </Card>

      {order && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px]">
              {t("admin.retouren.aanmaken_artikelen_titel", {
                ordernummer: order.ordernummer,
              })}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Hotfix 14-09: een lege lijst was niet van "deze order
                heeft geen regels" te onderscheiden en zei niets. De
                route levert nu de reden mee; zonder regels valt er
                niets te kiezen, dus dat hoort hier groot te staan. */}
            {order.artikelen.length === 0 && (
              <div
                role="alert"
                data-testid="artikelen-probleem"
                className="rounded-md border border-red-300 bg-red-50 px-3 py-2.5 text-[12.5px] text-red-900"
              >
                <div className="flex items-start gap-2">
                  <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold mb-0.5">
                      {t("admin.retouren.artikelen_leeg_titel")}
                    </div>
                    <p>
                      {t(
                        `admin.retouren.artikelen_leeg_${
                          order.artikelen_probleem ?? "onbekend"
                        }`,
                        {
                          defaultValue: t(
                            "admin.retouren.artikelen_leeg_onbekend",
                          ),
                        },
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {order.artikelen.map((artikel, index) => {
              const regel = regels[index] ?? LEGE_REGEL;
              const isSchade =
                regel.reden !== "" && SCHADE_REDENEN.includes(regel.reden);
              const isGeopendGevoelig =
                regel.reden !== "" &&
                GEOPEND_GEVOELIGE_REDENEN.includes(regel.reden);
              const toelichtingVerplicht =
                isSchade || regel.reden === "andere_reden";
              return (
                <div
                  key={artikel.index}
                  className="min-w-0 rounded-md border p-3 text-[13px]"
                  data-artikel={index}
                >
                  <label className="flex items-center gap-2.5 font-medium">
                    <input
                      type="checkbox"
                      checked={regel.geselecteerd}
                      onChange={(e) =>
                        wijzigRegel(index, {
                          geselecteerd: e.target.checked,
                        })
                      }
                    />
                    <span className="min-w-0 break-words">
                      {artikel.naam} ({formatteerBedrag(artikel.prijs_cents)}
                      {" · "}
                      {t("admin.retouren.aanmaken_besteld", {
                        aantal: artikel.aantal,
                      })}
                      )
                    </span>
                  </label>
                  {regel.geselecteerd && (
                    <div className="mt-3 min-w-0 space-y-3 sm:pl-6">
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="space-y-1">
                          <Label
                            htmlFor={`aanmaak-aantal-${index}`}
                            className="text-[12px]"
                          >
                            {t("admin.retouren.aanmaken_aantal")}
                          </Label>
                          <Input
                            id={`aanmaak-aantal-${index}`}
                            type="number"
                            min={1}
                            max={artikel.aantal}
                            value={regel.aantal}
                            onChange={(e) =>
                              wijzigRegel(index, {
                                aantal: Math.min(
                                  artikel.aantal,
                                  Math.max(1, Number(e.target.value) || 1),
                                ),
                              })
                            }
                            className="w-20"
                          />
                        </div>
                        <div className="w-full min-w-0 max-w-full space-y-1 sm:w-auto">
                          <Label
                            htmlFor={`aanmaak-reden-${index}`}
                            className="text-[12px]"
                          >
                            {t("retour.stap3.reden_label")}
                          </Label>
                          <select
                            id={`aanmaak-reden-${index}`}
                            className="h-9 w-full min-w-0 max-w-full rounded-md border border-input bg-white px-2 text-[13px]"
                            value={regel.reden}
                            onChange={(e) =>
                              wijzigRegel(index, {
                                reden: e.target
                                  .value as RetourRedenWaarde,
                                verpakkingGeopend: null,
                                toelichtingKeuze: "",
                                omschrijvingDuidelijk: null,
                              })
                            }
                          >
                            <option value="">
                              {t("retour.stap3.reden_kies")}
                            </option>
                            {ALLE_REDENEN.map((reden) => (
                              <option key={reden} value={reden}>
                                {t(`retour.redenen.${reden}`)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {isGeopendGevoelig && (
                        <fieldset>
                          <legend className="text-[12px] text-muted-foreground">
                            {t("retour.stap3.geopend_vraag")}
                          </legend>
                          <div className="mt-1 flex gap-4">
                            {[true, false].map((waarde) => (
                              <label
                                key={String(waarde)}
                                className="flex items-center gap-1.5"
                              >
                                <input
                                  type="radio"
                                  name={`aanmaak-geopend-${index}`}
                                  checked={
                                    regel.verpakkingGeopend === waarde
                                  }
                                  onChange={() =>
                                    wijzigRegel(index, {
                                      verpakkingGeopend: waarde,
                                    })
                                  }
                                />
                                {waarde
                                  ? t("retour.stap3.geopend_ja")
                                  : t("retour.stap3.geopend_nee")}
                              </label>
                            ))}
                          </div>
                        </fieldset>
                      )}

                      {regel.reden === "voldoet_niet_aan_verwachtingen" && (
                        <div className="flex min-w-0 flex-wrap gap-3">
                          <div className="w-full min-w-0 max-w-full space-y-1 sm:w-auto">
                            <Label
                              htmlFor={`aanmaak-keuze-${index}`}
                              className="text-[12px]"
                            >
                              {t("retour.stap3.verwachting_keuze_label")}
                            </Label>
                            <select
                              id={`aanmaak-keuze-${index}`}
                              className="h-9 w-full min-w-0 max-w-full rounded-md border border-input bg-white px-2 text-[13px]"
                              value={regel.toelichtingKeuze}
                              onChange={(e) =>
                                wijzigRegel(index, {
                                  toelichtingKeuze: e.target.value,
                                })
                              }
                            >
                              <option value="">
                                {t("retour.stap3.reden_kies")}
                              </option>
                              {[
                                "dier_lust_niet",
                                "kwaliteit",
                                "inhoud",
                                "anders",
                              ].map((keuze) => (
                                <option key={keuze} value={keuze}>
                                  {t(`retour.stap3.verwachting_${keuze}`)}
                                </option>
                              ))}
                            </select>
                          </div>
                          <fieldset>
                            <legend className="text-[12px] text-muted-foreground">
                              {t("retour.stap3.omschrijving_vraag")}
                            </legend>
                            <div className="mt-1 flex gap-4">
                              {[true, false].map((waarde) => (
                                <label
                                  key={String(waarde)}
                                  className="flex items-center gap-1.5"
                                >
                                  <input
                                    type="radio"
                                    name={`aanmaak-omschrijving-${index}`}
                                    checked={
                                      regel.omschrijvingDuidelijk === waarde
                                    }
                                    onChange={() =>
                                      wijzigRegel(index, {
                                        omschrijvingDuidelijk: waarde,
                                      })
                                    }
                                  />
                                  {waarde
                                    ? t("retour.stap3.omschrijving_ja")
                                    : t("retour.stap3.omschrijving_nee")}
                                </label>
                              ))}
                            </div>
                          </fieldset>
                        </div>
                      )}

                      <div className="space-y-1">
                        <Label
                          htmlFor={`aanmaak-toelichting-${index}`}
                          className="text-[12px]"
                        >
                          {t("retour.stap3.toelichting_label")}
                          {toelichtingVerplicht ? " *" : ""}
                        </Label>
                        <Textarea
                          id={`aanmaak-toelichting-${index}`}
                          value={regel.toelichting}
                          onChange={(e) =>
                            wijzigRegel(index, {
                              toelichting: e.target.value,
                            })
                          }
                          rows={2}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Sessie 171: de uitkomst vooraf, direct bij de artikelen.
                Een uitsluiting die JP opzij mag zetten krijgt hier zijn
                vinkje; niet in een los blok verderop. */}
            {voorafSleutel !== null || toonGeopendVinkje || toonTermijnVinkje ? (
              <div
                data-testid="vooraf"
                data-uitkomst={voorafData?.uitkomst ?? "onbekend"}
                data-uitsluiting={voorafData?.uitsluiting ?? ""}
                role="status"
                className={`min-w-0 space-y-2 rounded-md border px-3 py-2.5 text-[12.5px] ${
                  voorafData?.uitkomst === "label"
                    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                    : voorafData?.uitkomst === "beoordeling"
                      ? "border-sky-300 bg-sky-50 text-sky-900"
                      : voorafData
                        ? "border-amber-300 bg-amber-50 text-amber-900"
                        : "border-slate-200 bg-slate-50 text-slate-700"
                }`}
              >
                <div className="text-[12px] font-semibold">
                  {t("admin.retouren.vooraf_titel")}
                </div>
                {voorafSleutel === null ? null : !voorafActueel ||
                  vooraf.isFetching ? (
                  <p data-testid="vooraf-tekst">
                    {voorafData
                      ? voorafTekst()
                      : t("admin.retouren.vooraf_bezig")}
                  </p>
                ) : vooraf.isError ? (
                  <p data-testid="vooraf-fout">{t("admin.retouren.vooraf_fout")}</p>
                ) : (
                  <p data-testid="vooraf-tekst">{voorafTekst()}</p>
                )}
                {toonGeopendVinkje && (
                  <label className="flex items-start gap-2 text-slate-800">
                    <Checkbox
                      checked={geopendOverschrijven}
                      onCheckedChange={(v) => setGeopendOverschrijven(v === true)}
                      data-testid="vinkje-geopend"
                      className="mt-0.5"
                    />
                    <span className="min-w-0">
                      <span className="font-medium">
                        {t("admin.retouren.vooraf_geopend_vinkje")}
                      </span>
                      <br />
                      {t("admin.retouren.vinkje_geopend_uitleg")}
                    </span>
                  </label>
                )}
                {toonTermijnVinkje && (
                  <label className="flex items-start gap-2 text-slate-800">
                    <Checkbox
                      checked={termijnOverschrijven}
                      onCheckedChange={(v) => setTermijnOverschrijven(v === true)}
                      data-testid="vinkje-termijn"
                      className="mt-0.5"
                    />
                    <span className="min-w-0">
                      <span className="font-medium">
                        {t("admin.retouren.vooraf_termijn_vinkje")}
                      </span>
                      <br />
                      {t("admin.retouren.vinkje_termijn_uitleg")}
                    </span>
                  </label>
                )}
              </div>
            ) : null}

            <div className="flex min-w-0 flex-wrap gap-4 border-t pt-3">
              <div className="w-full min-w-0 max-w-full space-y-1 sm:w-auto">
                <Label htmlFor="aanmaak-oplossing" className="text-[12px]">
                  {t("retour.stap3.oplossing_titel")}
                </Label>
                <select
                  id="aanmaak-oplossing"
                  className="h-9 w-full min-w-0 max-w-full rounded-md border border-input bg-white px-2 text-[13px]"
                  value={oplossing}
                  onChange={(e) =>
                    setOplossing(e.target.value as RetourOplossingWaarde)
                  }
                >
                  {(
                    [
                      "terugbetaling",
                      "kortingscode",
                      "vervanging",
                    ] as RetourOplossingWaarde[]
                  ).map((waarde) => (
                    <option key={waarde} value={waarde}>
                      {t(`retour.stap3.oplossing_${waarde}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-full min-w-0 flex-1 space-y-1 sm:min-w-64">
                <Label htmlFor="aanmaak-opmerking" className="text-[12px]">
                  {t("admin.retouren.aanmaken_opmerking")}
                </Label>
                <Textarea
                  id="aanmaak-opmerking"
                  value={opmerking}
                  onChange={(e) => setOpmerking(e.target.value)}
                  placeholder={t(
                    "admin.retouren.aanmaken_opmerking_placeholder",
                  )}
                  rows={2}
                />
              </div>
            </div>

            {/* Besluit JP 14-09: "Klant niet mailen" blijft een losse
                keuze onderaan; de vinkjes die een uitsluiting opzij
                zetten staan sinds sessie 171 bij de uitkomst hierboven. */}
            <div className="space-y-2.5">
              <label className="flex items-start gap-2 text-[12.5px] text-slate-700">
                <Checkbox
                  checked={klantNietMailen}
                  onCheckedChange={(v) => setKlantNietMailen(v === true)}
                  data-testid="vinkje-niet-mailen"
                  className="mt-0.5"
                />
                <span className="min-w-0">
                  <span className="font-medium">
                    {t("admin.retouren.vinkje_niet_mailen")}
                  </span>
                  <br />
                  {t("admin.retouren.vinkje_niet_mailen_uitleg")}
                </span>
              </label>
              {klantNietMailen && (
                <label className="ml-6 flex items-start gap-2 text-[12.5px] text-slate-700">
                  <Checkbox
                    checked={ookGeenStatusmails}
                    onCheckedChange={(v) => setOokGeenStatusmails(v === true)}
                    data-testid="vinkje-geen-statusmails"
                    className="mt-0.5"
                  />
                  <span className="min-w-0">
                    <span className="font-medium">
                      {t("admin.retouren.vinkje_geen_statusmails")}
                    </span>
                    <br />
                    {t("admin.retouren.vinkje_geen_statusmails_uitleg")}
                  </span>
                </label>
              )}
            </div>

            {/* Hotfix 14-09: de knop stond uit zonder één woord uitleg.
                JP klikte, er gebeurde niets, en dat leest als een
                storing. Nu staat eronder wat er nog ontbreekt. */}
            <div className="space-y-1.5">
              <Button
                onClick={() => {
                  setResultaat(null);
                  aanmaken.mutate();
                }}
                disabled={
                  !compleet ||
                  aanmaken.isPending ||
                  // De retour staat al vast; opnieuw opslaan maakt een
                  // tweede. Herstellen gaat via "Opnieuw proberen".
                  resultaat?.uitkomst === "label_mislukt"
                }
              >
                {voorstelModus
                  ? t("admin.retouren.voorstel_bevestig")
                  : t("admin.retouren.aanmaken_bevestig")}
              </Button>
              {!compleet && (
                <p
                  className="text-[12.5px] text-amber-800"
                  data-testid="knop-uit-reden"
                >
                  {gekozen.length === 0
                    ? t("admin.retouren.knop_uit_geen_regel")
                    : t("admin.retouren.knop_uit_regel_incompleet", {
                        nummers: onvolledigeRegels.join(", "),
                      })}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
